import { CustomFieldType } from "@repo/app-types/entities/customField";
import type { CustomFieldCondition } from "@repo/app-types/entities/filterConditions";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { Input } from "@repo/ui/Inputs/input";
import { SimpleMenu } from "@repo/ui/Menu/simpleMenu";
import { SimpleMenuItem } from "@repo/ui/Menu/simpleMenuItem";
import { SimpleMenuPopover } from "@repo/ui/Menu/simpleMenuPopover";
import { type FC, useContext, useState } from "react";
import {
  RootMenuTriggerStateContext,
  SubmenuTrigger,
} from "react-aria-components";

export interface NumberCustomFieldConditionMenuProps {
  customFieldDefinitionId: string;
  onSelect: (condition: CustomFieldCondition) => void;
}

const comparisonOptions = [
  { operator: "=" as const, label: "is equal to..." },
  { operator: ">=" as const, label: "is at least..." },
  { operator: "<=" as const, label: "is at most..." },
];

export const NumberCustomFieldConditionMenu: FC<
  NumberCustomFieldConditionMenuProps
> = (props) => (
  <SimpleMenu aria-label="Number custom field condition">
    {comparisonOptions.map((option) => (
      <SubmenuTrigger key={option.operator}>
        <SimpleMenuItem>{option.label}</SimpleMenuItem>
        <SimpleMenuPopover>
          <NumberComparisonPicker {...props} operator={option.operator} />
        </SimpleMenuPopover>
      </SubmenuTrigger>
    ))}

    <SubmenuTrigger>
      <SimpleMenuItem>is between...</SimpleMenuItem>
      <SimpleMenuPopover>
        <NumberBetweenPicker {...props} />
      </SimpleMenuPopover>
    </SubmenuTrigger>

    <SimpleMenuItem
      onAction={() =>
        props.onSelect({
          customFieldDefinitionId: props.customFieldDefinitionId,
          operator: "has_any_value",
        })
      }
    >
      is not empty
    </SimpleMenuItem>
  </SimpleMenu>
);

interface NumberComparisonPickerProps extends NumberCustomFieldConditionMenuProps {
  operator: "=" | ">=" | "<=";
}

const NumberComparisonPicker: FC<NumberComparisonPickerProps> = (props) => {
  const rootMenuTriggerState = useContext(RootMenuTriggerStateContext);
  const [inputValue, setInputValue] = useState("");
  const value = parseNumber(inputValue);

  const handleApply = () => {
    if (value === null) return;

    props.onSelect({
      customFieldDefinitionId: props.customFieldDefinitionId,
      fieldType: CustomFieldType.Number,
      operator: props.operator,
      value,
    });
    rootMenuTriggerState?.close();
  };

  return (
    <div className="flex w-80 gap-2 px-3 py-2">
      <Input
        accessibleLabel="Number value"
        type="number"
        step="any"
        value={inputValue}
        onChange={(event) => setInputValue(event.target.value)}
        placeholder="Enter number"
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            handleApply();
          }
        }}
      />
      <Button
        size={Size.MEDIUM}
        variant={ButtonVariant.Primary}
        isDisabled={value === null}
        onClick={handleApply}
      >
        Apply
      </Button>
    </div>
  );
};

const NumberBetweenPicker: FC<NumberCustomFieldConditionMenuProps> = (
  props,
) => {
  const rootMenuTriggerState = useContext(RootMenuTriggerStateContext);
  const [minimumInput, setMinimumInput] = useState("");
  const [maximumInput, setMaximumInput] = useState("");
  const minimum = parseNumber(minimumInput);
  const maximum = parseNumber(maximumInput);
  const isRangeReversed =
    minimum !== null && maximum !== null && minimum > maximum;
  const canApply = minimum !== null && maximum !== null && !isRangeReversed;

  const handleApply = () => {
    if (!canApply) return;

    props.onSelect({
      customFieldDefinitionId: props.customFieldDefinitionId,
      fieldType: CustomFieldType.Number,
      operator: "between",
      minimum,
      maximum,
    });
    rootMenuTriggerState?.close();
  };

  return (
    <div className="w-80 px-3 py-2">
      <div className="grid grid-cols-2 gap-2">
        <Input
          accessibleLabel="Minimum value"
          label="Minimum"
          type="number"
          step="any"
          value={minimumInput}
          onChange={(event) => setMinimumInput(event.target.value)}
          placeholder="Enter number"
        />
        <Input
          accessibleLabel="Maximum value"
          label="Maximum"
          type="number"
          step="any"
          value={maximumInput}
          onChange={(event) => setMaximumInput(event.target.value)}
          placeholder="Enter number"
          errorDescription={
            isRangeReversed ? "Must be at least the minimum" : undefined
          }
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              handleApply();
            }
          }}
        />
      </div>
      <div className="mt-3 flex justify-end">
        <Button
          size={Size.MEDIUM}
          variant={ButtonVariant.Primary}
          isDisabled={!canApply}
          onClick={handleApply}
        >
          Apply
        </Button>
      </div>
    </div>
  );
};

function parseNumber(value: string): number | null {
  if (!value.trim()) return null;

  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : null;
}
